import { useCallback, useState } from 'react';
import {
  Control,
  FieldErrors,
  FieldValues,
  SubmitHandler,
  UseFormHandleSubmit,
  UseFormReset,
} from 'react-hook-form';

interface Props<T extends FieldValues> {
  control: Control<T>;
  dirtyFields: Object;
  errors: FieldErrors<T>;
  onSave: (data: T) => Promise<void>;
  handleSubmit: UseFormHandleSubmit<T, T>;
  isSaving: boolean;
  reset: UseFormReset<T>;
}

const useCollapsibleForm = <T extends FieldValues>({
  control,
  dirtyFields,
  errors,
  onSave,
  handleSubmit,
  isSaving,
  reset,
}: Props<T>) => {
  const [active, setActive] = useState<string | undefined>(undefined);

  const onCancel = useCallback(() => {
    reset();
    setActive(undefined);
  }, [reset]);

  const onEdit = useCallback((step: string) => {
    setActive(step);
  }, []);

  const onSubmit: SubmitHandler<T> = useCallback(
    async (data) => {
      try {
        await onSave(data);
        reset(data);
        setActive('');
      } catch (e) {
        // no-op
      }
    },
    [onSave, reset],
  );

  const handleOnSave = handleSubmit(
    onSubmit as T extends undefined
      ? SubmitHandler<T>
      : T extends FieldValues
        ? SubmitHandler<T>
        : never,
  );

  return {
    active,
    control,
    dirtyFields,
    errors,
    isSaving,
    onCancel,
    onEdit,
    onSave: handleOnSave,
  };
};

export default useCollapsibleForm;
